// Identidad de las filas del Modo Turno (#1449, revisor final vuelta 3,
// H-E/D-1 + D-6).
//
// EL PROBLEMA: el front escribía cada fila por (pestaña, `_row`), y `_row` es
// una POSICIÓN que el servidor REUSA a propósito (`append_row` hace max+1, ver
// backend `test_turno_row_reuse_h2.py`). Dos aparatos del mismo médico:
//   1. A escribe la evolución de X (fila N).
//   2. B borra a X: el autosave de A recibe 404 `row_not_found`.
//   3. B agrega a Y, que nace con el número N.
//   4. A sigue tipeando (en su pantalla X seguía abierto): su siguiente PATCH
//      salía a `row=N` → 200 → la evolución de X quedaba en la ficha de Y, y
//      `clearDraft` borraba el rastro. Cruce de PHI silencioso.
// La misma familia existía antes (D-6): el resultado de "Generar" se guardaba
// con la fila capturada al lanzarlo, y un borrador que no alcanzó a subir se
// reponía solo sobre el que heredó el número.
//
// EL CONTRATO: cada fila trae `_id` (el pk de `TurnoRow`, estable, nunca se
// reusa). Toda escritura de una fila existente manda `row_id` = ese `_id`. Si
// el número ya es de otra fila, el servidor responde 409 `row_mismatch` y NO
// escribe nada. Un 404 `row_not_found` o un 409 `row_mismatch` significan lo
// mismo para este lado: esa fila se eliminó en otro aparato. Desde ahí:
//   - cero PATCH más para esa identidad (ni el autosave, ni el flush);
//   - lo tipeado queda en el buffer con `rowGone` (nunca `clearDraft`);
//   - la fila sale de la pantalla y se avisa (cockpit desktop, mobile, tabla);
//   - "Usar lo mío" lo guarda como paciente EXTRA nuevo.
//
// Todo lo de acá es puro (sin React, sin red real): Turno.js no se monta en
// jest, así que la decisión vive afuera y se prueba sola.

/** `_id` de una fila del servidor, o null (fila sin identidad: backend viejo). */
export const identidadDeFila = (row) =>
  row && row._id !== undefined && row._id !== null && row._id !== ""
    ? row._id
    : null;

/** Compara identidades sin que "12" vs 12 cuente como distinto. */
export const mismaIdentidad = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/** Agrega `row_id` al payload solo si hay identidad (cliente/backend viejo: igual que antes). */
export const conRowId = (payload, rowId) =>
  rowId === null || rowId === undefined ? payload : { ...payload, row_id: rowId };

/**
 * ¿Este error significa "esa fila ya no es la que creías"? 404 `row_not_found`
 * (la fila no existe) y 409 `row_mismatch` (el número es de otro paciente).
 * Cualquier otro error (500, red, 409 `tab_not_found`) NO: ahí la fila puede
 * seguir viva y reintentar es lo correcto.
 */
export const esFilaEliminada = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  return (
    (status === 404 && code === "row_not_found") ||
    (status === 409 && code === "row_mismatch")
  );
};

/**
 * ¿Este error es un rechazo de escritura condicional (compare-and-set,
 * revisor externo ronda 7, 28-sep-2026)? 409 `valor_cambio`: la FILA es la
 * correcta (row_id coincidió o no vino), pero el CONTENIDO que el cliente
 * leyó para esa columna ya no es el actual — otro aparato la escribió en el
 * medio. Distinto de `esFilaEliminada`: acá el paciente sigue siendo el
 * mismo, no hay que sacar la fila de pantalla ni vetar su identidad. No
 * reintenta (reintentar con el mismo `expected` viejo fallaría igual) y no
 * pisa (el servidor no escribió nada).
 */
export const esValorCambio = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  return status === 409 && code === "valor_cambio";
};

/** Clave del número eliminado en `numerosEliminados` (por pestaña). */
export const claveNumero = (tab, rowNumber) => `${tab}:${rowNumber}`;

// ── Identidad CAPTURADA al empezar (#1449, R-1/R-2/R-3 del revisor v2) ─────
//
// La regla: la identidad que viaja en una escritura es la de la fila que el
// médico tenía en la mano CUANDO EMPEZÓ (al abrir el campo o el editor, al
// lanzar la descarga), nunca la de quien ocupa el número al confirmar. El
// refresco de fondo puede cambiar a X por Y en el mismo número sin que el
// editor se entere; si la identidad se leyera al guardar, saldría la de Y y el
// servidor aceptaría el texto de X en la ficha de Y.

/** `{ rowNumber, rowId }` de una fila, para capturarla al empezar una edición. */
export const capturarIdentidad = (row) =>
  row && row._row ? { rowNumber: row._row, rowId: identidadDeFila(row) } : null;

/**
 * Clave primitiva de la identidad de una fila (número + `_id`). Cambia cuando
 * el mismo lugar pasa a mostrar a otro paciente: los editores la comparan con
 * la que capturaron al abrirse para saber que ya no son de quien era.
 */
export const claveIdentidad = (row) =>
  row && row._row ? `${row._row}:${identidadDeFila(row) ?? ""}` : "";

/**
 * Destino de una escritura que viene de un editor: la identidad que capturó
 * al abrirse; sin captura (un llamador que no la manda), la fila activa.
 */
export const destinoDeEscritura = (capturada, filaActiva) =>
  capturada && capturada.rowNumber ? capturada : capturarIdentidad(filaActiva);

/**
 * La fila sobre la que se aplica algo cuya identidad se capturó al empezar
 * (R-3: el "Actualizar" del aviso de exámenes, que llega decenas de segundos
 * después de lanzar la descarga). Busca por número y exige esa identidad.
 * `null` = el número ya muestra a otro paciente, o no hay fila (se borró, o
 * la pantalla es otra pestaña): no se escribe. Sin identidad capturada
 * (backend viejo) o sin identidad en pantalla, la del número, como antes.
 */
export const filaConIdentidadCapturada = (rows, rowNumber, rowIdCapturada) => {
  const fila = (rows || []).find((r) => r && r._row === rowNumber) || null;
  if (!fila) return null;
  if (rowIdCapturada === null || rowIdCapturada === undefined || rowIdCapturada === "") return fila;
  const enPantalla = identidadDeFila(fila);
  if (enPantalla === null) return fila;
  return mismaIdentidad(enPantalla, rowIdCapturada) ? fila : null;
};

// ── Visualizador de exámenes (#1449 R-4, revisor final v4 D4-1) ────────────
//
// El visor y su descarga trabajaban por NÚMERO de fila. A abre los exámenes
// de X (fila 4) y la descarga (30-90 s) queda corriendo; B borra a X y agrega
// a Y, que hereda el 4. Al terminar, el HTML de X se asignaba al 4 (ya Y), el
// visor seguía rotulado con X, y "Traspasar" buscaba la fila por número →
// PATCH row=4 row_id=<Y> → 200: los exámenes de X en la ficha de Y, sin aviso.
// Ahora la identidad del paciente (`_id` + RUT) se captura al lanzar la
// descarga o al apuntar el visor, y todo lo que llega después se asocia a
// ESA identidad, nunca al número.

/** `{ rowNumber, rowId, rut }` del paciente al lanzar la descarga o apuntar el visor. */
export const identidadDeVisor = (row, rut = "") =>
  row && row._row
    ? { rowNumber: row._row, rowId: identidadDeFila(row), rut: String(rut || "") }
    : null;

/**
 * La fila que hoy es del paciente capturado por `identidadDeVisor`, o null.
 *   - Con `_id` en la captura y en la pantalla, decide el `_id` (el mismo
 *     paciente con el RUT corregido sigue siendo él; otra fila con el mismo
 *     RUT no lo es).
 *   - Sin `_id` en alguno de los dos lados (backend viejo), decide el RUT del
 *     lanzamiento contra el de la fila, si los dos lo tienen.
 *   - Sin captura, o sin fila en ese número (se borró): null. Nunca "la del
 *     número" por omisión: ese era el cruce.
 * `rutDe(fila)` extrae el RUT igual que al capturar (Turno.js pasa el suyo).
 */
export const filaDelPaciente = (rows, capturada, rutDe) => {
  if (!capturada || !capturada.rowNumber) return null;
  const fila = (rows || []).find((r) => r && r._row === capturada.rowNumber) || null;
  if (!fila) return null;
  const idPantalla = identidadDeFila(fila);
  const idCapturada =
    capturada.rowId === null || capturada.rowId === undefined || capturada.rowId === ""
      ? null
      : capturada.rowId;
  if (idPantalla !== null && idCapturada !== null) {
    return mismaIdentidad(idPantalla, idCapturada) ? fila : null;
  }
  const rutPantalla = typeof rutDe === "function" ? String(rutDe(fila) || "") : "";
  if (capturada.rut && rutPantalla && rutPantalla !== capturada.rut) return null;
  return fila;
};

/**
 * Decide con qué identidad sale una escritura sobre `rowNumber`, o si no tiene
 * que salir.
 *
 *   - `rowIdPedida`: la identidad que el llamador CAPTURÓ (Generar al lanzarse,
 *     el diálogo al abrirse, el dictado al empezar). Manda sobre la pantalla:
 *     si el número hoy muestra a otro paciente, la escritura no sale.
 *   - sin `rowIdPedida`: la de la fila que hoy muestra la pantalla con ese
 *     número (la que el médico está viendo mientras escribe).
 *   - `eliminadas`: identidades que el servidor ya dijo que no existen.
 *   - `numerosEliminados`: números que quedaron sin fila en pantalla por un
 *     404/409. Cubre el instante entre sacar la fila y que llegue la siguiente:
 *     un flush del editor que se desmonta no puede salir sin identidad.
 *
 * Devuelve `{ eliminada, rowId }`. `eliminada: true` = no mandar nada.
 */
export const resolverIdentidadEscritura = ({
  rows,
  rowNumber,
  rowIdPedida = null,
  eliminadas,
  numerosEliminados,
  tab,
}) => {
  const enEliminadas = (id) => Boolean(eliminadas && id != null && eliminadas.has(String(id)));
  const fila = (rows || []).find((r) => r && r._row === rowNumber) || null;
  const enPantalla = identidadDeFila(fila);
  if (rowIdPedida !== null && rowIdPedida !== undefined) {
    if (enEliminadas(rowIdPedida)) return { eliminada: true, rowId: rowIdPedida };
    // El número ya muestra a otro paciente: lo que se capturó era de uno que
    // no está más en ese número.
    if (enPantalla !== null && !mismaIdentidad(enPantalla, rowIdPedida)) {
      return { eliminada: true, rowId: rowIdPedida };
    }
    return { eliminada: false, rowId: rowIdPedida };
  }
  if (enPantalla !== null) {
    if (enEliminadas(enPantalla)) return { eliminada: true, rowId: enPantalla };
    return { eliminada: false, rowId: enPantalla };
  }
  if (!fila && numerosEliminados && numerosEliminados.has(claveNumero(tab, rowNumber))) {
    return { eliminada: true, rowId: numerosEliminados.get(claveNumero(tab, rowNumber)) ?? null };
  }
  return { eliminada: false, rowId: null };
};

/**
 * Refresco de fondo (`loadRows` soft): ¿qué hacer con la fila local `vieja`
 * frente a la del servidor `nueva` con el MISMO número?
 *
 *   "misma"    → misma identidad (o sin identidad): el merge de siempre
 *                (gana el servidor salvo las celdas sucias o con el cursor).
 *   "conservar"→ el número ya es de OTRO paciente y el médico tiene esa fila
 *                sucia o con el cursor adentro: se deja la fila local ENTERA
 *                (incluido su `_id`). Mezclar columnas pegaría el texto de X
 *                sobre Y, y el siguiente guardado saldría con la identidad de
 *                Y. Con la de X, el servidor responde 409 y se trata como
 *                eliminada.
 *   "reemplazar" → otro paciente y nadie la está editando: se muestra la del
 *                servidor tal cual, sin arrastrar nada de la local.
 */
export const decidirFusionSoft = (vieja, nueva, enEdicion) => {
  const idVieja = identidadDeFila(vieja);
  const idNueva = identidadDeFila(nueva);
  if (idVieja === null || idNueva === null || mismaIdentidad(idVieja, idNueva)) return "misma";
  return enEdicion ? "conservar" : "reemplazar";
};

/** Saca de una lista las filas cuya identidad el servidor ya dio por eliminada. */
export const sinFilasEliminadas = (rows, eliminadas) => {
  if (!eliminadas || !eliminadas.size) return rows;
  return (rows || []).filter((r) => {
    const id = identidadDeFila(r);
    return id === null || !eliminadas.has(String(id));
  });
};

/**
 * Filas que el médico tenía en edición (sucias o con el cursor) y que el
 * refresco de fondo ya no trae, ni su número ni su identidad: se borraron en
 * otro aparato mientras escribía. Se detecta ANTES de pisar `rows`, para
 * marcarlas como eliminadas antes de que el editor se desmonte y haga su
 * flush. Si el número sigue viniendo (con otro paciente), NO se incluye: ese
 * caso lo resuelve `decidirFusionSoft` ("conservar") y el 409 del guardado.
 */
export const filasEnEdicionQueDesaparecen = (locales, delServidor, enEdicion) => {
  const idsServidor = new Set(
    (delServidor || []).map(identidadDeFila).filter((id) => id !== null).map(String),
  );
  const numerosServidor = new Set((delServidor || []).filter(Boolean).map((r) => r._row));
  return (locales || []).filter((r) => {
    const id = identidadDeFila(r);
    if (id === null || idsServidor.has(String(id))) return false;
    if (numerosServidor.has(r._row)) return false;
    return enEdicion(r._row);
  });
};

/**
 * Acciones de un bulk (censo, mover cama, alta sobre cama ocupada) con la
 * identidad de la fila sobre la que se calcularon. `rows` tiene que ser la
 * MISMA lista que usó quien armó las acciones (el reconciliador, el diálogo):
 * tomar la identidad de otra lista (una más nueva) sería validar contra un
 * estado que la acción nunca vio. Solo se agrega a las acciones que escriben
 * una fila existente (UPDATE, MOVE_TO_EXTRA, DELETE_FIXED) y solo si la fila
 * trae `_id`.
 */
export const accionesConIdentidad = (actions, rows) => {
  const porNumero = new Map((rows || []).filter(Boolean).map((r) => [r._row, r]));
  return (actions || []).map((a) => {
    if (!a || !["UPDATE", "MOVE_TO_EXTRA", "DELETE_FIXED"].includes(a.type)) return a;
    if (a.row_id !== undefined && a.row_id !== null) return a;
    const id = identidadDeFila(porNumero.get(a.sheet_row_number));
    return id === null ? a : { ...a, row_id: id };
  });
};

/**
 * El guardado de UNA celda (lo que hace `saveCell` en Turno.js), sin React.
 *
 * `deps`:
 *   - `rows()`: filas que muestra la pantalla ahora.
 *   - `eliminadas` (Set de ids) y `numerosEliminados` (Map clave → id).
 *   - `patch(payload)`: el PATCH real (Turno.js le suma unidad/pestaña).
 *   - `stageDraft`, `clearDraft`: el buffer write-ahead.
 *   - `onFilaEliminada({ rowNumber, rowId, column })`: sacar la fila de la
 *     pantalla y avisar. Idempotente.
 *
 * `expected` (compare-and-set, revisor externo ronda 7, 28-sep-2026)
 * opcional: el valor que el llamador LEYÓ para esta columna antes de calcular
 * `value`. Solo lo mandan los caminos que calculan su escritura a partir de
 * un texto leído antes de varios `await` (reordenar por plantilla, re-sembrar
 * esqueletos vacíos) — el autosave normal de tipeo no lo necesita.
 *
 * Devuelve `{ ok, motivo, enviado, rowId, response?, error? }`:
 *   - `ok: true`: el servidor confirmó; el borrador se borró.
 *   - `motivo: "fila_eliminada"`: 404/409 `row_mismatch` ahora o antes; el
 *     borrador queda con `rowGone` y NO hay que volver a marcar la celda
 *     sucia (reintentar solo repetiría el rechazo, o peor, caería en el
 *     paciente que heredó el número).
 *   - `motivo: "valor_cambio"`: 409 `valor_cambio` — la fila es la correcta,
 *     pero el contenido que se leyó ya no es el actual (otro aparato lo
 *     escribió en el medio). NO es `fila_eliminada`: no se saca la fila de
 *     pantalla ni se veta su identidad. El borrador SE LIMPIA (`clearDraft`,
 *     ronda 8: no hay nada válido que recuperar, el valor rechazado no es
 *     del médico) y viene `rowServidor` (la fila real, del cuerpo del 409)
 *     para que el llamador repinte; no reintenta con el mismo `expected`.
 *   - `motivo: "error"`: cualquier otro error (red, 500); el borrador queda
 *     y se reintenta.
 */
export const guardarCeldaConIdentidad = async (
  { tab, tabId = null, rowNumber, column, value, rowIdPedida = null, expected },
  deps,
) => {
  const ident = resolverIdentidadEscritura({
    rows: deps.rows(),
    rowNumber,
    rowIdPedida,
    eliminadas: deps.eliminadas,
    numerosEliminados: deps.numerosEliminados,
    tab,
  });
  const meta = { tabId, ...(ident.rowId !== null && ident.rowId !== undefined ? { rowId: ident.rowId } : {}) };
  if (ident.eliminada) {
    deps.stageDraft(tab, rowNumber, column, value, { ...meta, rowGone: true });
    deps.onFilaEliminada({ rowNumber, rowId: ident.rowId, column });
    return { ok: false, motivo: "fila_eliminada", enviado: false, rowId: ident.rowId };
  }
  // Write-ahead: el valor queda en localStorage ANTES de salir a la red.
  deps.stageDraft(tab, rowNumber, column, value, meta);
  try {
    let payload = conRowId({ tab, row: rowNumber, values: { [column]: value } }, ident.rowId);
    if (expected !== undefined) {
      payload = { ...payload, expected: { [column]: expected } };
    }
    const response = await deps.patch(payload);
    deps.clearDraft(tab, rowNumber, column);
    return { ok: true, motivo: "", enviado: true, rowId: ident.rowId, response };
  } catch (error) {
    if (esFilaEliminada(error)) {
      if (ident.rowId !== null && ident.rowId !== undefined && deps.eliminadas) {
        deps.eliminadas.add(String(ident.rowId));
      }
      // H8 + H-E: la marca fuerza el conflicto "fila eliminada" al recuperar,
      // sin importar quién ocupe hoy ese número. Nunca `clearDraft`.
      deps.stageDraft(tab, rowNumber, column, value, { ...meta, rowGone: true });
      deps.onFilaEliminada({ rowNumber, rowId: ident.rowId, column });
      return { ok: false, motivo: "fila_eliminada", enviado: true, rowId: ident.rowId, error };
    }
    if (esValorCambio(error)) {
      // NO es fila_eliminada: la fila sigue siendo la misma, solo cambió su
      // contenido. No se marca `rowGone`, no se veta la identidad, no sale
      // de pantalla.
      //
      // Revisor externo, ronda 8 (28-sep-2026, BLOQUEANTE de la ronda 7): el
      // valor que quedó pintado en pantalla y en el borrador es el que el
      // SERVIDOR ACABA DE RECHAZAR — sin esto, la próxima carga completa
      // ofrece "recuperar mi texto" sobre una fila que el médico nunca
      // escribió, y "Usar lo mío" pisaría la nota real del otro aparato.
      // `clearDraft` saca ese rastro (no hay `rowGone`: la fila sigue siendo
      // válida, solo con OTRO contenido). `rowServidor` viaja en el cuerpo
      // del 409 (nunca en logs, ver `_valor_cambio_response` en el back) para
      // que el llamador repinte SIN un GET aparte.
      deps.clearDraft(tab, rowNumber, column);
      return {
        ok: false, motivo: "valor_cambio", enviado: true, rowId: ident.rowId, error,
        rowServidor: error?.response?.data?.row ?? null,
      };
    }
    return { ok: false, motivo: "error", enviado: true, rowId: ident.rowId, error };
  }
};

/**
 * Qué hacer con el `resultado` de `guardarCeldaConIdentidad` (lo que hace
 * `saveCell` en Turno.js), fuera del componente para poder probarlo con
 * comportamiento real — Turno.js no se monta en jest.
 *
 * Revisor externo, ronda 8 (28-sep-2026, mutación X4 de la ronda 7):
 * `saveCell` decidía esto con una cadena de `if` INLINE en el componente, y
 * una mutación que neutralizaba SOLO la rama de `valor_cambio` (sin tocar
 * esta función) hacía que la suite completa siguiera en verde — la celda
 * volvía a quedar sucia y el siguiente autosave salía SIN `expected`,
 * exactamente el pisado entre aparatos que el CAS vino a cerrar. Sacando la
 * decisión acá, un test directo de esta función mide el comportamiento real.
 *
 * Devuelve `{ ok, marcarSucia, mensajeError }`:
 *   - `ok`: el servidor confirmó.
 *   - `marcarSucia`: true SOLO para un error genérico (red, 500) — ahí sí
 *     conviene reintentar más tarde. `fila_eliminada` y `valor_cambio` NUNCA
 *     marcan sucio: reintentar repetiría el rechazo (o, sin `expected`
 *     fresco, pisaría lo que el otro aparato acaba de escribir).
 *   - `mensajeError`: para `setRowError`, o "" si no corresponde avisar acá
 *     (el llamador de `valor_cambio`/`fila_eliminada` decide su propio aviso
 *     con `opts.motivo`, ver `handleReorganizeTemplate`).
 */
export const decidirTrasGuardarCelda = (resultado) => {
  if (resultado.ok) return { ok: true, marcarSucia: false, mensajeError: "" };
  if (resultado.motivo === "fila_eliminada" || resultado.motivo === "valor_cambio") {
    return { ok: false, marcarSucia: false, mensajeError: "" };
  }
  return {
    ok: false, marcarSucia: true,
    mensajeError: "No pudimos guardar los cambios. Quedaron respaldados en este dispositivo y se reintentan solos.",
  };
};

/**
 * Aplica la fila `updated` (la que devolvió el servidor, en un 200 o en el
 * `row` de un 409 `valor_cambio`) sobre `rows`, con las MISMAS reglas que ya
 * usaba el `setRows` inline de `saveCell`:
 *   - solo la fila con el mismo `_row`;
 *   - solo si la identidad coincide (si las dos la traen) — si mientras
 *     volvía la respuesta la pantalla pasó a mostrar a otro paciente en ese
 *     número, lo que vuelve no se le pega;
 *   - nunca pisa una columna que el médico tiene sucia AHORA MISMO (la está
 *     escribiendo, o hay otro guardado suyo en vuelo) — `dirtyColumns` es el
 *     `Set` de `"col"` sucias de ESA fila al momento de repintar.
 *
 * Pura: sin React, sin red. `rows` no se muta, se devuelve un array nuevo (o
 * el MISMO `rows` si no hay nada que tocar, para no gatillar un re-render).
 */
export const aplicarFilaDelServidor = (rows, updated, dirtyColumns = new Set()) => {
  if (!updated || updated._row === undefined) return rows;
  let tocado = false;
  const next = (rows || []).map((item) => {
    if (!item || item._row !== updated._row) return item;
    if (
      identidadDeFila(updated) !== null &&
      identidadDeFila(item) !== null &&
      !mismaIdentidad(identidadDeFila(updated), identidadDeFila(item))
    ) {
      return item;
    }
    const nextItem = { ...item };
    let cambio = false;
    Object.entries(updated).forEach(([col, val]) => {
      if (col === "_row") {
        if (nextItem._row !== updated._row) { nextItem._row = updated._row; cambio = true; }
        return;
      }
      if (dirtyColumns.has(col)) return;
      if (nextItem[col] !== val) { nextItem[col] = val; cambio = true; }
    });
    if (!cambio) return item;
    tocado = true;
    return nextItem;
  });
  return tocado ? next : rows;
};
